import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { buildDisplayNameMap, buildPortfolioCategories } from '../../../utils/portfolioNames';
import api from '../../../services/api';

const COLOURS = {
  primary: '#072040',
  secondary: '#f0a500',
  success: '#91CF9B',
  info: '#599BE3',
  light: '#E7E6E6',
  white: '#ffffff',
};

// Hardcoded fallback — used only when registry hasn't loaded yet
const FALLBACK_DISPLAY_NAMES = {
  Cash: 'Cash',
  DDP: 'Drummond Dynamic Portfolio',
  DPM: 'Drummond Private Markets Portfolio',
  DPC: 'Drummond Private Income Portfolio',
  DS30: 'Drummond Strategic 30',
  DS50: 'Drummond Strategic 50',
  DS70: 'Drummond Strategic 70',
  DS90: 'Drummond Strategic 90',
  DS100: 'Drummond Strategic 100',
  DS100Plus: 'Drummond Strategic 100+',
  FirstChoiceConservative: 'First Choice Conservative',
  FirstChoiceModerate: 'First Choice Moderate',
  FirstChoiceBalanced: 'First Choice Balanced',
  FirstChoiceGrowth: 'First Choice Growth',
  FirstChoiceHighGrowth: 'First Choice High Growth',
  GovernmentBonds: 'Government Bonds',
  Credit: 'Credit',
  AustralianEquities: 'Australian Equities',
  GlobalEquitiesHedged: 'Global Equities (Hedged)',
  GlobalEquitiesUnhedged: 'Global Equities (Unhedged)',
  EmergingMarketEquities: 'Emerging Market Equities',
  HedgeFunds: 'Hedge Funds',
  REITs: 'REITs',
  Infrastructure: 'Infrastructure',
};

const getDisplayName = (key) => FALLBACK_DISPLAY_NAMES[key] || key;

const APPROACHES = [
  {
    key: 'risk',
    title: 'Risk Profile',
    description: 'Enter your desired growth and cash exposure to build a blended portfolio',
    icon: 'bi-shield-check',
  },
  {
    key: 'bucket',
    title: 'Bucketing Strategy',
    description: 'Allocate across cash, mid-risk, and growth buckets',
    icon: 'bi-stack',
  },
  {
    key: 'objective',
    title: 'Objective Based',
    description: 'Predefined portfolios optimised for specific outcomes',
    icon: 'bi-bullseye',
  },
  {
    key: 'custom',
    title: 'Custom',
    description: 'Build your own portfolio allocation',
    icon: 'bi-sliders',
  },
];

/* ─── Portfolio Summary Section ─── */
const PortfolioSummary = ({ weights, growthExposure, expectedReturn, volatility, formData }) => {
  const totalWeight = Object.values(weights || {}).reduce(
    (sum, w) => sum + (parseFloat(w) || 0), 0
  );
  const totalPct = (totalWeight * 100).toFixed(1);
  const isValid = Math.abs(totalWeight - 1) <= 0.005;
  const portfolioGrowthPct = growthExposure != null ? (growthExposure * 100).toFixed(1) : null;

  // Compute annuity growth contribution
  // Annuity sits ON TOP of financial assets (not carved out)
  const financialAssets = parseFloat(formData?.financial_assets) || 0;
  const annuityPurchase = parseFloat(formData?.income_stream_asset_test) || 0;
  const annuityType = formData?.annuity_type || 'inflation_adjusted';
  const refPortfolio = formData?.annuity_reference_portfolio || '';

  let annuityGrowthExposure = 0.0;
  if (annuityType === 'market_linked' && refPortfolio) {
    annuityGrowthExposure = ANNUITY_GROWTH_EXPOSURE[refPortfolio] || 0.0;
  }

  const excludeAnnuity = formData?.exclude_annuity_from_growth || false;
  const hasAnnuity = annuityPurchase > 0 && !excludeAnnuity;
  const totalAssets = financialAssets + annuityPurchase;
  const portfolioProportion = totalAssets > 0 ? financialAssets / totalAssets : 1;
  const annuityProportion = totalAssets > 0 ? annuityPurchase / totalAssets : 0;

  // Weighted contributions to total growth (as % of total assets)
  const portfolioGrowthContribution = growthExposure != null ? portfolioProportion * growthExposure : null;
  const annuityGrowthContribution = annuityProportion * annuityGrowthExposure;
  const totalGrowth = portfolioGrowthContribution != null
    ? portfolioGrowthContribution + annuityGrowthContribution
    : null;

  const totalGrowthPct = totalGrowth != null ? (totalGrowth * 100).toFixed(1) : null;
  const annuityGrowthPct = (annuityGrowthContribution * 100).toFixed(1);

  return (
    <div className="card mb-3">
      <div className="card-header bg-light">
        <h6 className="mb-0 fw-bold">Portfolio Summary</h6>
      </div>
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="mb-2">
              <h6 className="mb-2">
                Total Allocation: <span style={{ color: isValid ? COLOURS.success : '#dc3545' }}>{totalPct}%</span>
              </h6>
              <div className="progress" style={{ height: '20px' }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${Math.min(parseFloat(totalPct), 100)}%`,
                    backgroundColor: isValid ? COLOURS.success : '#dc3545',
                  }}
                >
                  {totalPct}%
                </div>
              </div>
              {!isValid && (
                <div className="text-danger mt-1">
                  <i className="bi bi-exclamation-triangle"></i> Total allocation must equal 100%
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-2" style={{ marginTop: '22px' }}>
              <h6 className="mb-2">
                {hasAnnuity ? 'Total Growth Exposure' : 'Growth Exposure'}: {hasAnnuity ? (totalGrowthPct != null ? `${totalGrowthPct}%` : '-') : (portfolioGrowthPct != null ? `${portfolioGrowthPct}%` : '-')}
              </h6>
              <div className="progress" style={{ height: '20px' }}>
                {hasAnnuity && totalGrowth != null ? (
                  <>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${Math.min(portfolioGrowthContribution * 100, 100)}%`,
                        backgroundColor: COLOURS.info,
                      }}
                      title={`Portfolio: ${(portfolioGrowthContribution * 100).toFixed(1)}%`}
                    ></div>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${Math.min(annuityGrowthContribution * 100, 100 - portfolioGrowthContribution * 100)}%`,
                        backgroundColor: COLOURS.secondary,
                      }}
                      title={`Annuity: ${annuityGrowthPct}%`}
                    ></div>
                  </>
                ) : (
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${portfolioGrowthPct || 0}%`,
                      backgroundColor: COLOURS.info,
                    }}
                  ></div>
                )}
              </div>
              <div className="d-flex justify-content-between mt-1">
                <small>No Growth</small>
                <small>Balanced</small>
                <small>All Growth</small>
              </div>
            </div>
          </div>
        </div>

        {/* Growth exposure breakdown when annuity is present */}
        {hasAnnuity && (
          <div className="row mt-3 pt-3 border-top">
            <div className="col-12">
              <h6 className="fw-bold mb-2">Growth Exposure Breakdown</h6>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th className="text-end">Growth Exposure</th>
                    <th className="text-end">Weight of Total Assets</th>
                    <th className="text-end">Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="d-inline-block me-2" style={{ width: 12, height: 12, backgroundColor: COLOURS.info, borderRadius: 2 }}></span>
                      Portfolio
                    </td>
                    <td className="text-end">{portfolioGrowthPct != null ? `${portfolioGrowthPct}%` : '-'}</td>
                    <td className="text-end">{(portfolioProportion * 100).toFixed(1)}%</td>
                    <td className="text-end">{portfolioGrowthContribution != null ? `${(portfolioGrowthContribution * 100).toFixed(1)}%` : '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="d-inline-block me-2" style={{ width: 12, height: 12, backgroundColor: COLOURS.secondary, borderRadius: 2 }}></span>
                      Annuity
                    </td>
                    <td className="text-end">{(annuityGrowthExposure * 100).toFixed(1)}%</td>
                    <td className="text-end">{(annuityProportion * 100).toFixed(1)}%</td>
                    <td className="text-end">{annuityGrowthPct}%</td>
                  </tr>
                  <tr className="fw-bold" style={{ borderTop: '2px solid #dee2e6' }}>
                    <td>Total</td>
                    <td className="text-end"></td>
                    <td className="text-end">100.0%</td>
                    <td className="text-end">{totalGrowthPct != null ? `${totalGrowthPct}%` : '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="row align-items-center mt-3 pt-3 border-top">
          <div className="col-md-6">
            <div className="mb-2">
              <h6 className="mb-2">
                <i className="bi bi-graph-up me-2 text-success"></i>
                Expected Return: {expectedReturn != null ? `${(expectedReturn * 100).toFixed(1)}% p.a.` : '-'}
              </h6>
              <small className="text-muted">Weighted average annual return</small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-2">
              <h6 className="mb-2">
                <i className="bi bi-bar-chart me-2" style={{ color: COLOURS.secondary }}></i>
                Volatility: {volatility != null ? `${(volatility * 100).toFixed(1)}% p.a.` : '-'}
              </h6>
              <small className="text-muted">Standard deviation of returns</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Portfolio Allocation Table ─── */
const PortfolioAllocationTable = ({ weights, resolveName }) => {
  // Prefer the registry-backed resolver (applies per-domain branded names);
  // fall back to the module-level hardcoded map when not supplied.
  const nameOf = resolveName || getDisplayName;
  const entries = Object.entries(weights || {}).filter(([, w]) => w && parseFloat(w) > 0);
  if (entries.length === 0) return null;

  return (
    <div className="card mb-3">
      <div className="card-header bg-light">
        <h6 className="mb-0 fw-bold">Portfolio Allocation</h6>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Portfolio</th>
                <th className="text-end">Allocation (%)</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([name, weight]) => (
                <tr key={name}>
                  <td>{nameOf(name)}</td>
                  <td className="text-end">{(parseFloat(weight) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {entries.length > 0 && (
          <div className="mt-2 text-muted small">
            Growth Exposure: {
              (() => {
                const totalWeight = entries.reduce((s, [, w]) => s + parseFloat(w), 0);
                return totalWeight > 0 ? `${(totalWeight * 100).toFixed(1)}% allocated` : '-';
              })()
            }
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Bucketing growth threshold ─── */
// Portfolios with growth exposure below this go in the Bucketing "Mid-Risk"
// dropdown; at or above it, the "Growth" dropdown. Reproduces the historical DS
// split (DS30/DS50/DDP/DPC → mid, DS70/DS90/DS100(+) → growth).
const GROWTH_BUCKET_MIN = 0.6;

/* ─── Growth exposure (client-side estimate) ───
   Per-portfolio growth is supplied data-driven via `growthMap` (built from the
   /available-portfolios `growth_exposure`), not a hardcoded taxonomy. The backend
   /portfolio-volatility endpoint remains the authoritative figure shown in the UI;
   this estimate only seeds formData while that request is in flight. */
const calculateGrowthExposure = (weights, growthMap = {}) => {
  const entries = Object.entries(weights || {}).filter(([, w]) => w && parseFloat(w) > 0);
  if (entries.length === 0) return null;
  const totalWeight = entries.reduce((s, [, w]) => s + parseFloat(w), 0);
  if (totalWeight === 0) return null;
  let growth = 0;
  entries.forEach(([name, w]) => {
    const ge = growthMap[name] ?? 0.5;
    growth += (parseFloat(w) / totalWeight) * ge;
  });
  return growth;
};

// Industry-standard reference portfolio growth exposures for market-linked
// annuities. These come from annuity provider PDS documents (Cash_Index =
// 100% cash; Conservative/Balanced/Growth indices map to standard industry
// growth-asset splits). NOT part of the Drummond SAC taxonomy.
//
// Must stay in sync with the backend copy at
// `app/apps/retirement/calculator.py:typical_portfolio_growth_exposure` —
// if you edit one, edit both. Consider promoting to an admin-managed table
// if the list grows beyond ~10 entries.
const ANNUITY_GROWTH_EXPOSURE = {
  Cash_Index: 0.0,
  Conservative_Index: 0.30,
  Conservative_Balanced_Index: 0.50,
  Balanced_Index: 0.65,
  Growth_Index: 0.85,
};

/**
 * Solver: given targetGrowth (0–1), targetCash (0–1) and a growth-sorted ladder of
 * non-cash portfolios (`nonCash` = [{name, growth}] ascending, from the domain's
 * accessible universe), produce portfolio weights by interpolating between the two
 * bracketing portfolios. Returns { weights, isCornerSolution, effectiveGrowth, growthExposure }.
 */
const solvePortfolioWeights = (targetGrowth, targetCash, formData, excludeAnnuity, nonCash = []) => {
  let adjustedGrowth = targetGrowth;

  if (!excludeAnnuity) {
    const financialAssets = parseFloat(formData.financial_assets) || 0;
    const annuityPurchase = parseFloat(formData.income_stream_asset_test) || 0;
    const annuityType = formData.annuity_type || 'inflation_adjusted';
    const refPortfolio = formData.annuity_reference_portfolio || '';

    let annuityGrowthExposure = 0.0;
    if (annuityType === 'market_linked' && refPortfolio) {
      annuityGrowthExposure = ANNUITY_GROWTH_EXPOSURE[refPortfolio] || 0.0;
    }

    // Annuity sits ON TOP of financial assets (not carved out)
    // totalAssets = financialAssets + annuityPurchase
    // targetGrowth applies to totalAssets, portfolio must compensate
    if (financialAssets > 0 && annuityPurchase > 0) {
      const totalAssets = financialAssets + annuityPurchase;
      adjustedGrowth = (targetGrowth * totalAssets - annuityPurchase * annuityGrowthExposure) / financialAssets;
    }
  }

  const nonCashWeight = 1 - targetCash;
  if (nonCashWeight <= 0) {
    return {
      weights: { Cash: 1.0 },
      isCornerSolution: false,
      effectiveGrowth: 0,
      requiredGrowth: 0,
      growthExposure: 0,
    };
  }

  const effectiveGrowthNeeded = adjustedGrowth / nonCashWeight;
  let isCornerSolution = false;
  let actualEffectiveGrowth = effectiveGrowthNeeded;
  const weights = {};

  if (targetCash > 0) {
    weights.Cash = targetCash;
  }

  // `nonCash` is the growth-sorted accessible ladder (Cash already excluded). If the
  // universe is empty (still loading / no permissioned portfolios), hold in cash.
  if (nonCash.length === 0) {
    return {
      weights: targetCash > 0 ? { Cash: targetCash } : {},
      isCornerSolution: false, cornerPortfolio: null,
      effectiveGrowth: 0, requiredGrowth: effectiveGrowthNeeded, growthExposure: 0,
    };
  }

  let cornerPortfolio = null;

  if (effectiveGrowthNeeded <= nonCash[0].growth) {
    // Corner: below DS30
    isCornerSolution = effectiveGrowthNeeded < nonCash[0].growth;
    cornerPortfolio = nonCash[0].name;
    weights[nonCash[0].name] = nonCashWeight;
    actualEffectiveGrowth = nonCash[0].growth;
  } else if (effectiveGrowthNeeded >= nonCash[nonCash.length - 1].growth) {
    // Corner: above DS100
    isCornerSolution = effectiveGrowthNeeded > nonCash[nonCash.length - 1].growth;
    cornerPortfolio = nonCash[nonCash.length - 1].name;
    weights[nonCash[nonCash.length - 1].name] = nonCashWeight;
    actualEffectiveGrowth = nonCash[nonCash.length - 1].growth;
  } else {
    // Find two adjacent portfolios that bracket the effective growth
    for (let i = 0; i < nonCash.length - 1; i++) {
      const lower = nonCash[i];
      const upper = nonCash[i + 1];
      if (effectiveGrowthNeeded >= lower.growth && effectiveGrowthNeeded <= upper.growth) {
        const span = upper.growth - lower.growth;
        const upperFraction = span > 0 ? (effectiveGrowthNeeded - lower.growth) / span : 0;
        const lowerFraction = 1 - upperFraction;
        weights[lower.name] = lowerFraction * nonCashWeight;
        weights[upper.name] = upperFraction * nonCashWeight;
        actualEffectiveGrowth = effectiveGrowthNeeded;
        break;
      }
    }
  }

  // Overall growth exposure = non-cash weight * actual effective growth
  const growthExposure = nonCashWeight * actualEffectiveGrowth;

  return { weights, isCornerSolution, cornerPortfolio, effectiveGrowth: actualEffectiveGrowth, requiredGrowth: effectiveGrowthNeeded, growthExposure };
};

/* ─── Risk Profile Portfolio View (Solver-based) ─── */
const RiskProfileView = ({ formData, updateFormData, resolveName, ladder = [] }) => {
  const [desiredGrowth, setDesiredGrowth] = useState(() => {
    const current = formData.current_portfolio_growth_exposure;
    return current ? String(Math.round(current * 100)) : '70';
  });
  const [desiredCash, setDesiredCash] = useState('5');

  // Extract only the fields the solver needs to avoid circular formData dependency
  const solverFinancialAssets = parseFloat(formData.financial_assets) || 0;
  const solverAnnuityPurchase = parseFloat(formData.income_stream_asset_test) || 0;
  const solverAnnuityType = formData.annuity_type || 'inflation_adjusted';
  const solverRefPortfolio = formData.annuity_reference_portfolio || '';
  const solverExcludeAnnuity = formData.exclude_annuity_from_growth || false;

  const solverResult = useMemo(() => {
    const tGrowth = Math.max(0, Math.min(100, parseInt(desiredGrowth) || 0)) / 100;
    const tCash = Math.max(0, Math.min(100, parseInt(desiredCash) || 0)) / 100;
    const solverFormData = {
      financial_assets: solverFinancialAssets,
      income_stream_asset_test: solverAnnuityPurchase,
      annuity_type: solverAnnuityType,
      annuity_reference_portfolio: solverRefPortfolio,
    };
    return solvePortfolioWeights(tGrowth, tCash, solverFormData, solverExcludeAnnuity, ladder);
  }, [desiredGrowth, desiredCash, solverFinancialAssets, solverAnnuityPurchase, solverAnnuityType, solverRefPortfolio, solverExcludeAnnuity, ladder]);

  useEffect(() => {
    updateFormData({
      portfolio_weights: solverResult.weights,
      portfolio_type: 'risk',
      portfolio_id: null,
      current_portfolio_growth_exposure: solverResult.growthExposure,
    });
  }, [solverResult, updateFormData]);

  return (
    <div>
      <h5>Risk Profile Portfolio</h5>
      <p className="text-muted mb-3">
        Enter your desired growth and cash exposure. The solver will blend your available portfolios to match your targets.
      </p>
      {ladder.length === 0 && (
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          No portfolios are available for your account to build a risk-profile blend.
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label">
            Desired Growth Exposure (%){' '}
            <i
              className="bi bi-info-circle text-muted"
              title="The target percentage of your total portfolio allocated to growth assets. 0% = all defensive, 100% = all growth."
              style={{ cursor: 'help' }}
            ></i>
          </label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            value={desiredGrowth}
            onChange={(e) => setDesiredGrowth(e.target.value)}
            onBlur={() => setDesiredGrowth(String(Math.max(0, Math.min(100, parseInt(desiredGrowth) || 0))))}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">
            Desired Cash Exposure (%){' '}
            <i
              className="bi bi-info-circle text-muted"
              title="The proportion of all your financial assets which is cash, including TDs, cash at bank, and cash on platform. Don't include cash in managed account model portfolios."
              style={{ cursor: 'help' }}
            ></i>
          </label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            value={desiredCash}
            onChange={(e) => setDesiredCash(e.target.value)}
            onBlur={() => setDesiredCash(String(Math.max(0, Math.min(100, parseInt(desiredCash) || 0))))}
          />
        </div>
      </div>

      {solverResult.isCornerSolution && (
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          The required non-cash growth exposure ({(solverResult.requiredGrowth * 100).toFixed(1)}%) is outside the available range of portfolio growth exposures.{' '}
          {solverResult.cornerPortfolio} has been applied as the closest available portfolio.
        </div>
      )}

      <PortfolioAllocationTable weights={solverResult.weights} resolveName={resolveName} />
    </div>
  );
};

/* ─── Bucketing Strategy View ─── */
const BucketStrategyView = ({ formData, updateFormData, universe = [], growthMap = {}, resolveName }) => {
  const [cashPct, setCashPct] = useState('10');
  const [midRiskPct, setMidRiskPct] = useState('40');
  const [growthPct, setGrowthPct] = useState('50');
  const [midRiskPortfolio, setMidRiskPortfolio] = useState('');
  const [growthPortfolio, setGrowthPortfolio] = useState('');

  // Split the domain's accessible universe into the two buckets by growth exposure.
  const midRiskOptions = useMemo(
    () => universe.filter((p) => p.growth < GROWTH_BUCKET_MIN),
    [universe],
  );
  const growthOptions = useMemo(
    () => universe.filter((p) => p.growth >= GROWTH_BUCKET_MIN),
    [universe],
  );

  // Default / re-sync the selected portfolio when the options change (universe loads).
  useEffect(() => {
    if (midRiskOptions.length && !midRiskOptions.some((o) => o.name === midRiskPortfolio)) {
      setMidRiskPortfolio(midRiskOptions[0].name);
    }
  }, [midRiskOptions, midRiskPortfolio]);
  useEffect(() => {
    if (growthOptions.length && !growthOptions.some((o) => o.name === growthPortfolio)) {
      setGrowthPortfolio(growthOptions[0].name);
    }
  }, [growthOptions, growthPortfolio]);

  const bucketWeights = useMemo(() => {
    const c = Math.max(0, Math.min(100, parseInt(cashPct) || 0));
    const m = Math.max(0, Math.min(100, parseInt(midRiskPct) || 0));
    const g = Math.max(0, Math.min(100, parseInt(growthPct) || 0));

    const weights = {};
    if (c > 0) weights.Cash = c / 100;
    if (m > 0 && midRiskPortfolio) weights[midRiskPortfolio] = m / 100;
    if (g > 0 && growthPortfolio) weights[growthPortfolio] = g / 100;
    return weights;
  }, [cashPct, midRiskPct, growthPct, midRiskPortfolio, growthPortfolio]);

  const totalPct = useMemo(() => {
    const c = parseInt(cashPct) || 0;
    const m = parseInt(midRiskPct) || 0;
    const g = parseInt(growthPct) || 0;
    return c + m + g;
  }, [cashPct, midRiskPct, growthPct]);

  useEffect(() => {
    const growthExposure = calculateGrowthExposure(bucketWeights, growthMap) || 0;
    updateFormData({
      portfolio_weights: bucketWeights,
      portfolio_type: 'bucket',
      portfolio_id: null,
      current_portfolio_growth_exposure: growthExposure,
    });
  }, [bucketWeights, growthMap, updateFormData]);

  const bucketStyle = {
    container: {
      border: `2px solid ${COLOURS.primary}`,
      borderRadius: '4px 4px 16px 16px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    header: (color) => ({
      backgroundColor: color,
      color: COLOURS.white,
      padding: '10px 14px',
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '14px',
    }),
    body: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      position: 'relative',
      padding: '12px',
      minHeight: '180px',
    },
    fill: (pct, color) => ({
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: `${Math.min(Math.max(pct, 0), 100)}%`,
      backgroundColor: color,
      opacity: 0.15,
      transition: 'height 0.3s ease',
      borderRadius: '0 0 14px 14px',
    }),
    pctLabel: {
      fontSize: '28px',
      fontWeight: 'bold',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
      marginBottom: '8px',
    },
    controls: {
      position: 'relative',
      zIndex: 1,
    },
  };

  const cashVal = parseInt(cashPct) || 0;
  const midVal = parseInt(midRiskPct) || 0;
  const growthVal = parseInt(growthPct) || 0;

  return (
    <div>
      <h5>Bucketing Strategy</h5>
      <p className="text-muted mb-3">
        Allocate your portfolio across three buckets. Percentages must total 100%.
      </p>

      <div className="row g-3 mb-3">
        {/* Cash Bucket */}
        <div className="col-md-4">
          <div style={bucketStyle.container}>
            <div style={bucketStyle.header(COLOURS.info)}>
              <i className="bi bi-cash-coin me-2"></i>Cash
            </div>
            <div style={bucketStyle.body}>
              <div style={bucketStyle.fill(cashVal, COLOURS.info)}></div>
              <div style={bucketStyle.pctLabel}>
                <span style={{ color: COLOURS.primary }}>{cashVal}%</span>
              </div>
              <div style={bucketStyle.controls}>
                <label className="form-label small mb-1">Allocation (%)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min="0"
                  max="100"
                  value={cashPct}
                  onChange={(e) => setCashPct(e.target.value)}
                  onBlur={() => setCashPct(String(Math.max(0, Math.min(100, parseInt(cashPct) || 0))))}
                />
                {/* Spacer to align with portfolio dropdown in other buckets */}
                <label className="form-label small mb-1 mt-2" style={{ visibility: 'hidden' }}>Portfolio</label>
                <div style={{ height: '31px' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Mid-Risk Bucket */}
        <div className="col-md-4">
          <div style={bucketStyle.container}>
            <div style={bucketStyle.header(COLOURS.secondary)}>
              <i className="bi bi-shield-check me-2"></i>Mid-Risk
            </div>
            <div style={bucketStyle.body}>
              <div style={bucketStyle.fill(midVal, COLOURS.secondary)}></div>
              <div style={bucketStyle.pctLabel}>
                <span style={{ color: COLOURS.primary }}>{midVal}%</span>
              </div>
              <div style={bucketStyle.controls}>
                <label className="form-label small mb-1">Allocation (%)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min="0"
                  max="100"
                  value={midRiskPct}
                  onChange={(e) => setMidRiskPct(e.target.value)}
                  onBlur={() => setMidRiskPct(String(Math.max(0, Math.min(100, parseInt(midRiskPct) || 0))))}
                />
                <label className="form-label small mb-1 mt-2">Portfolio</label>
                <select
                  className="form-select form-select-sm"
                  value={midRiskPortfolio}
                  onChange={(e) => setMidRiskPortfolio(e.target.value)}
                >
                  {midRiskOptions.map((opt) => (
                    <option key={opt.name} value={opt.name}>{resolveName ? resolveName(opt.name) : opt.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Growth Bucket */}
        <div className="col-md-4">
          <div style={bucketStyle.container}>
            <div style={bucketStyle.header(COLOURS.success)}>
              <i className="bi bi-graph-up-arrow me-2"></i>Growth
            </div>
            <div style={bucketStyle.body}>
              <div style={bucketStyle.fill(growthVal, COLOURS.success)}></div>
              <div style={bucketStyle.pctLabel}>
                <span style={{ color: COLOURS.primary }}>{growthVal}%</span>
              </div>
              <div style={bucketStyle.controls}>
                <label className="form-label small mb-1">Allocation (%)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min="0"
                  max="100"
                  value={growthPct}
                  onChange={(e) => setGrowthPct(e.target.value)}
                  onBlur={() => setGrowthPct(String(Math.max(0, Math.min(100, parseInt(growthPct) || 0))))}
                />
                <label className="form-label small mb-1 mt-2">Portfolio</label>
                <select
                  className="form-select form-select-sm"
                  value={growthPortfolio}
                  onChange={(e) => setGrowthPortfolio(e.target.value)}
                >
                  {growthOptions.map((opt) => (
                    <option key={opt.name} value={opt.name}>{resolveName ? resolveName(opt.name) : opt.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Validation */}
      {totalPct !== 100 && (
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Bucket allocations total {totalPct}% — must equal 100%.
        </div>
      )}

      <PortfolioAllocationTable weights={bucketWeights} resolveName={resolveName} />
    </div>
  );
};

/* ─── Objectives Based Portfolio View ───
   The allocation is optimised LIVE against the current default CMA scenario via
   POST /api/retirement/objective-portfolio — nothing is read from a precomputed
   table. `objectiveOptions` only supplies the feasible dropdown grid. */
const ObjectivesView = ({ objectiveOptions, formData, updateFormData, resolveName }) => {
  const targetReturns = useMemo(
    () => objectiveOptions.map((o) => o.target_return).sort((a, b) => a - b),
    [objectiveOptions]
  );

  const [selectedReturn, setSelectedReturn] = useState(() => targetReturns[0] || 0);

  const cashOptions = useMemo(() => {
    const opt = objectiveOptions.find(
      (o) => parseFloat(o.target_return) === parseFloat(selectedReturn)
    );
    return (opt?.cash_percentages || []).slice().sort((a, b) => a - b);
  }, [objectiveOptions, selectedReturn]);

  const [selectedCash, setSelectedCash] = useState(() => cashOptions[0] || 0);

  // Keep the cash selection valid when the target (and so its feasible cash
  // levels) changes.
  useEffect(() => {
    if (cashOptions.length > 0 && !cashOptions.includes(selectedCash)) {
      setSelectedCash(cashOptions[0]);
    }
  }, [cashOptions, selectedCash]);

  const [result, setResult] = useState(null); // { feasible, weights, message, ... }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  // Optimise live whenever the target return or cash percentage changes.
  useEffect(() => {
    if (selectedReturn == null || selectedCash == null) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    setError('');
    debounceRef.current = setTimeout(() => {
      api
        .post('/api/retirement/objective-portfolio', {
          target_return: selectedReturn,
          cash_percentage: selectedCash,
        })
        .then((res) => {
          const data = res.data;
          setResult(data);
          if (data.feasible) {
            updateFormData({
              portfolio_id: `objective-${selectedReturn}-${selectedCash}`,
              portfolio_type: 'objective',
              portfolio_weights: data.weights || {},
              current_portfolio_growth_exposure: data.growth_exposure ?? 0,
            });
          } else {
            // Infeasible — clear the selection so the user can't proceed.
            updateFormData({ portfolio_id: null, portfolio_type: 'objective', portfolio_weights: {} });
          }
        })
        .catch((err) => {
          setResult(null);
          setError(err.response?.data?.detail || 'Could not optimise this portfolio.');
          updateFormData({ portfolio_id: null, portfolio_type: 'objective', portfolio_weights: {} });
        })
        .finally(() => setLoading(false));
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectedReturn, selectedCash, updateFormData]);

  return (
    <div>
      <h5>Objectives Based Portfolio</h5>
      <p className="text-muted mb-3">
        Select your target return and cash percentage to create an optimised portfolio.
      </p>
      <div className="alert alert-info mb-3">
        <small>
          <strong>Note:</strong> This portfolio is optimised live to minimise volatility (risk) while
          meeting your target return requirement, using the current capital market assumptions. This
          provides the least risky way to achieve your desired return.
        </small>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Target Return (%)</label>
          <select
            className="form-select"
            value={selectedReturn}
            onChange={(e) => setSelectedReturn(parseFloat(e.target.value))}
          >
            {targetReturns.map((tr) => (
              <option key={tr} value={tr}>
                {(tr * 100).toFixed(1)}%
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">
            Cash Percentage (%){' '}
            <i
              className="bi bi-info-circle text-muted"
              title="The proportion of all your financial assets which is cash, including TDs, cash at bank, and cash on platform. Don't include cash in managed account model portfolios."
              style={{ cursor: 'help' }}
            ></i>
          </label>
          <select
            className="form-select"
            value={selectedCash}
            onChange={(e) => setSelectedCash(parseInt(e.target.value))}
          >
            {cashOptions.map((cp) => (
              <option key={cp} value={cp}>
                {cp}%
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-muted mb-2">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          Optimising portfolio…
        </div>
      )}

      {!loading && error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && result && !result.feasible && (
        <div className="alert alert-warning">{result.message}</div>
      )}

      {!loading && !error && result?.feasible && (
        <PortfolioAllocationTable weights={result.weights} resolveName={resolveName} />
      )}
    </div>
  );
};

/* ─── Fallback portfolio category definitions for optgroups ─── */
const FALLBACK_CATEGORIES = [
  {
    label: 'Strategic Series',
    keys: ['DS30', 'DS50', 'DS70', 'DS90', 'DS100', 'DS100Plus', 'DDP'],
  },
  {
    label: 'FirstChoice Series',
    keys: ['FirstChoiceConservative', 'FirstChoiceModerate', 'FirstChoiceBalanced', 'FirstChoiceGrowth', 'FirstChoiceHighGrowth'],
  },
  {
    label: 'Private Market',
    keys: ['DPM', 'DPC'],
  },
  // No Asset Classes fallback — sourced live from the SAC taxonomy via
  // buildPortfolioCategories. SACs appear once the registry has loaded.
];

/* ─── Custom Portfolio Builder ─── */
const CustomPortfolioBuilder = ({ availablePortfolios, formData, updateFormData, portfolioRegistry, growthMap = {} }) => {
  const rowIdRef = useRef(0);
  const [rows, setRows] = useState(() => {
    const existing = formData.portfolio_weights || {};
    const initial = Object.entries(existing)
      .filter(([, w]) => w && parseFloat(w) > 0)
      .map(([name, w]) => ({ id: ++rowIdRef.current, portfolioName: name, weight: parseFloat((parseFloat(w) * 100).toFixed(2)) }));
    return initial.length > 0 ? initial : [];
  });

  const availableKeys = useMemo(
    () => new Set(availablePortfolios.map((p) => p.name)),
    [availablePortfolios]
  );

  // Build display name map and categories from registry (with fallback)
  const displayNames = useMemo(
    () => portfolioRegistry ? buildDisplayNameMap(portfolioRegistry) : FALLBACK_DISPLAY_NAMES,
    [portfolioRegistry]
  );
  const getDisplayName = (key) => displayNames[key] || key;

  const categories = useMemo(
    () => buildPortfolioCategories(portfolioRegistry, availableKeys) || FALLBACK_CATEGORIES,
    [portfolioRegistry, availableKeys]
  );

  const selectedNames = useMemo(
    () => new Set(rows.filter((r) => r.portfolioName).map((r) => r.portfolioName)),
    [rows]
  );

  useEffect(() => {
    const newWeights = {};
    rows.forEach((r) => {
      if (r.portfolioName && r.weight > 0) {
        newWeights[r.portfolioName] = (newWeights[r.portfolioName] || 0) + r.weight / 100;
      }
    });
    updateFormData({
      portfolio_weights: newWeights,
      portfolio_type: 'custom',
      portfolio_id: null,
      current_portfolio_growth_exposure: calculateGrowthExposure(newWeights, growthMap) || 0,
    });
  }, [rows, growthMap, updateFormData]);

  const addRow = () => {
    setRows((prev) => [...prev, { id: ++rowIdRef.current, portfolioName: '', weight: 0 }]);
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (field === 'weight') {
          const num = parseFloat(value) || 0;
          return { ...r, weight: Math.max(0, Math.min(100, num)) };
        }
        return { ...r, [field]: value };
      })
    );
  };

  const buildOptions = (currentRowName) => {
    return categories.map((cat) => {
      // buildPortfolioCategories already filters portfolio codes against
      // availableKeys. It does NOT filter the Asset Classes group because
      // /available-portfolios only enumerates blender codes, never SACs.
      // Re-applying the filter here would silently strip the taxonomy SACs.
      const options = cat.keys
        .filter((k) => k === currentRowName || !selectedNames.has(k));
      if (options.length === 0) return null;
      return (
        <optgroup key={cat.label} label={cat.label}>
          {options.map((k) => (
            <option key={k} value={k}>
              {getDisplayName(k)}
            </option>
          ))}
        </optgroup>
      );
    }).filter(Boolean);
  };

  const totalWeight = rows.reduce((s, r) => s + (r.weight || 0), 0);
  const BAR_COLOURS = [COLOURS.primary, COLOURS.info, COLOURS.secondary, COLOURS.success, '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  return (
    <div>
      <h5>Custom Portfolio</h5>
      <p className="text-muted mb-3">
        Build your custom portfolio by adding and allocating portfolios. Total allocation must equal 100%.
      </p>

      {/* Stacked allocation bar */}
      {rows.length > 0 && (
        <div className="mb-3">
          <div style={{ display: 'flex', height: '28px', borderRadius: '6px', overflow: 'hidden', border: `1px solid ${COLOURS.light}` }}>
            {rows.filter(r => r.portfolioName && r.weight > 0).map((row, i) => (
              <div
                key={row.id}
                style={{
                  width: `${row.weight}%`,
                  backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length],
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
                title={`${getDisplayName(row.portfolioName)}: ${row.weight}%`}
              >
                {row.weight >= 8 && (
                  <span style={{ color: COLOURS.white, fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {row.weight}%
                  </span>
                )}
              </div>
            ))}
            {totalWeight < 100 && (
              <div style={{ flex: 1, backgroundColor: COLOURS.light, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '11px', color: COLOURS.gray }}>
                  {(100 - totalWeight).toFixed(0)}% unallocated
                </span>
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="d-flex flex-wrap gap-3 mt-2">
            {rows.filter(r => r.portfolioName && r.weight > 0).map((row, i) => (
              <div key={row.id} className="d-flex align-items-center">
                <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], display: 'inline-block', marginRight: 6 }}></span>
                <small className="text-muted">{getDisplayName(row.portfolioName)}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table-style rows */}
      {rows.length > 0 && (
        <div className="table-responsive mb-3">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr style={{ backgroundColor: COLOURS.light }}>
                <th style={{ width: '45%' }}>Portfolio</th>
                <th style={{ width: '30%' }}>Allocation (%)</th>
                <th style={{ width: '15%' }} className="text-center">Weight</th>
                <th style={{ width: '10%' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <span style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], display: 'inline-block', marginRight: 10, flexShrink: 0 }}></span>
                      <select
                        className="form-select form-select-sm"
                        value={row.portfolioName}
                        onChange={(e) => updateRow(row.id, 'portfolioName', e.target.value)}
                        style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none', paddingLeft: 0 }}
                      >
                        <option value="">Select portfolio...</option>
                        {buildOptions(row.portfolioName)}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        min="0"
                        max="100"
                        value={row.weight || ''}
                        placeholder="0"
                        onChange={(e) => updateRow(row.id, 'weight', e.target.value)}
                        style={{ paddingRight: '28px' }}
                      />
                      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: COLOURS.gray, fontSize: '13px', pointerEvents: 'none' }}>%</span>
                    </div>
                  </td>
                  <td className="text-center">
                    <div
                      style={{
                        width: '100%',
                        height: 6,
                        backgroundColor: COLOURS.light,
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(row.weight || 0, 100)}%`,
                          height: '100%',
                          backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length],
                          borderRadius: 3,
                          transition: 'width 0.3s ease',
                        }}
                      ></div>
                    </div>
                  </td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => removeRow(row.id)}
                      title="Remove"
                      style={{ color: '#dc3545', padding: '2px 6px' }}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length === 0 && (
        <div className="text-center py-4 mb-3" style={{ backgroundColor: COLOURS.light, borderRadius: '8px' }}>
          <i className="bi bi-pie-chart" style={{ fontSize: '32px', color: COLOURS.gray }}></i>
          <p className="text-muted mt-2 mb-0">No portfolios added yet. Click below to start building.</p>
        </div>
      )}

      <div className="text-center mb-3">
        <button
          type="button"
          className="btn text-white"
          style={{ backgroundColor: COLOURS.primary }}
          onClick={addRow}
        >
          <i className="bi bi-plus-circle me-1"></i> Add Portfolio
        </button>
      </div>
    </div>
  );
};

/* ─── Main StepPortfolio Component ─── */
const StepPortfolio = ({
  approach,
  setApproach,
  formData,
  updateFormData,
  nextStep,
  prevStep,
  availablePortfolios,
  riskUniverse,
  riskPortfolios,
  objectiveOptions,
  portfolioRegistry,
  isLocked = false,
}) => {
  // Backend-computed volatility and expected return
  const [covVolatility, setCovVolatility] = useState(null);
  const [covExpReturn, setCovExpReturn] = useState(null);
  const [covGrowth, setCovGrowth] = useState(null);
  const debounceRef = useRef(null);

  // Registry-backed display-name resolver, shared by all four approach views so
  // per-domain branded names appear in the Risk/Bucket/Objective allocation tables
  // and pickers (not just Custom). Layered over the hardcoded fallback so non-registry
  // blocks (Cash, SAC taxonomy codes) keep their friendly names.
  const resolveName = useMemo(() => {
    const reg = portfolioRegistry ? buildDisplayNameMap(portfolioRegistry) : {};
    return (key) => reg[key] || FALLBACK_DISPLAY_NAMES[key] || key;
  }, [portfolioRegistry]);

  // Data-driven portfolio universe for the Risk / Bucket / Custom modes: the domain's
  // accessible portfolios (from /available-portfolios), each with its bottom-up growth
  // exposure. `ladder` is the growth-sorted non-cash list the Risk solver interpolates
  // over; `growthMap` (incl. Cash=0) feeds the client-side growth estimate. Replaces the
  // old hardcoded DS taxonomy so tailored clients blend their own portfolios.
  const ladder = useMemo(
    () => (availablePortfolios || [])
      .filter((p) => p.name !== 'Cash' && p.growth_exposure != null)
      .map((p) => ({ name: p.name, growth: p.growth_exposure }))
      .sort((a, b) => a.growth - b.growth),
    [availablePortfolios],
  );
  // Risk Profile uses its own universe — the Strategic Series only for standard
  // (non-tailored) clients (from /risk-universe). Bucketing/Custom keep `ladder`.
  const riskLadder = useMemo(
    () => (riskUniverse || [])
      .filter((p) => p.name !== 'Cash' && p.growth_exposure != null)
      .map((p) => ({ name: p.name, growth: p.growth_exposure }))
      .sort((a, b) => a.growth - b.growth),
    [riskUniverse],
  );
  const growthMap = useMemo(() => {
    const m = { Cash: 0 };
    (availablePortfolios || []).forEach((p) => {
      if (p.growth_exposure != null) m[p.name] = p.growth_exposure;
    });
    return m;
  }, [availablePortfolios]);

  // Fetch volatility from backend whenever weights change
  useEffect(() => {
    if (!['risk', 'custom', 'bucket', 'objective'].includes(approach)) {
      setCovVolatility(null);
      setCovExpReturn(null);
      setCovGrowth(null);
      return;
    }
    const weights = formData.portfolio_weights || {};
    const nonZero = {};
    Object.entries(weights).forEach(([name, w]) => {
      const val = parseFloat(w);
      if (val > 0) nonZero[name] = val;
    });
    if (Object.keys(nonZero).length === 0) {
      setCovVolatility(null);
      setCovExpReturn(null);
      setCovGrowth(null);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post('/api/retirement/portfolio-volatility', { weights: nonZero })
        .then((res) => {
          setCovVolatility(res.data.volatility);
          setCovExpReturn(res.data.expected_return);
          setCovGrowth(res.data.growth_exposure ?? null);
        })
        .catch(() => {
          setCovVolatility(null);
          setCovExpReturn(null);
          setCovGrowth(null);
        });
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [approach, formData.portfolio_weights]);

  const activePortfolio = useMemo(() => {
    // Objective portfolios are now weights-driven like custom/bucket: the live
    // optimiser sets formData.portfolio_weights, and return/vol/growth are
    // recomputed from those weights via /portfolio-volatility (cov* state).
    if (['risk', 'custom', 'bucket', 'objective'].includes(approach)) {
      const weights = formData.portfolio_weights || {};
      const hasData = Object.values(weights).some((w) => parseFloat(w) > 0);
      // Growth exposure for custom/bucket/objective comes from the backend
      // (bottom-up from the SAC taxonomy), not the legacy frontend map which
      // defaulted unknown sub-asset classes to 50%.
      const growthExp = approach === 'risk'
        ? formData.current_portfolio_growth_exposure ?? null
        : (hasData ? covGrowth : null);
      return {
        weights,
        growthExposure: growthExp,
        expectedReturn: hasData ? covExpReturn : null,
        volatility: hasData ? covVolatility : null,
      };
    }
    return { weights: {}, growthExposure: null, expectedReturn: null, volatility: null };
  }, [approach, formData, availablePortfolios, covVolatility, covExpReturn, covGrowth]);

  const canProceed = () => {
    if (!approach) return false;
    if (['risk', 'custom', 'bucket', 'objective'].includes(approach)) {
      const total = Object.values(formData.portfolio_weights || {}).reduce(
        (sum, w) => sum + (parseFloat(w) || 0), 0
      );
      return Math.abs(total - 1) <= 0.005;
    }
    return formData.portfolio_id != null;
  };

  return (
    <div>
      {isLocked && (
        <div className="alert alert-warning mb-3 d-flex align-items-center">
          <i className="bi bi-lock-fill me-2"></i>
          <span>
            <strong>View Only:</strong> This scenario is locked. Click "Make Changes" above to edit.
          </span>
        </div>
      )}
      <fieldset disabled={isLocked} style={{ opacity: isLocked ? 0.7 : 1 }}>
      {/* Approach Selection Cards */}
      <div className="card mb-3">
        <div className="card-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white, border: 'none' }}>
          <h6 className="mb-0">
            <i className="bi bi-compass me-2"></i>Investment Approach
          </h6>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            How would you like to select your investment portfolio?
          </p>
          <div className="row g-3 mb-3">
            {APPROACHES.map((item) => {
              const isSelected = approach === item.key;
              return (
                <div key={item.key} className="col-md-3">
                  <div
                    className={`philosophy-card h-100${isSelected ? ' selected' : ''}`}
                    onClick={() => setApproach(item.key)}
                  >
                    <div className="philosophy-icon-circle">
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    <h5 className="fw-bold" style={{ fontSize: '16px' }}>
                      {item.title}
                    </h5>
                    <p className="small mb-0">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Portfolio Configuration (conditional on approach) */}
      {approach && (
        <div className="card mb-3">
          <div className="card-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white, border: 'none' }}>
            <h6 className="mb-0">
              <i className="bi bi-pie-chart me-2"></i>Portfolio Allocation
            </h6>
          </div>
          <div className="card-body">
            {approach === 'risk' && (
              <RiskProfileView
                formData={formData}
                updateFormData={updateFormData}
                resolveName={resolveName}
                ladder={riskLadder}
              />
            )}

            {approach === 'bucket' && (
              <BucketStrategyView
                formData={formData}
                updateFormData={updateFormData}
                universe={ladder}
                growthMap={growthMap}
                resolveName={resolveName}
              />
            )}

            {approach === 'objective' && (
              <ObjectivesView
                objectiveOptions={objectiveOptions}
                formData={formData}
                updateFormData={updateFormData}
                resolveName={resolveName}
              />
            )}

            {approach === 'custom' && (
              <CustomPortfolioBuilder
                availablePortfolios={availablePortfolios}
                formData={formData}
                updateFormData={updateFormData}
                portfolioRegistry={portfolioRegistry}
                growthMap={growthMap}
              />
            )}

            {(parseFloat(formData.income_stream_asset_test) || 0) > 0 && (
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="exclude_annuity_growth"
                  checked={formData.exclude_annuity_from_growth || false}
                  onChange={(e) => updateFormData({ exclude_annuity_from_growth: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="exclude_annuity_growth">
                  Exclude annuity from growth calculation{' '}
                  <i
                    className="bi bi-info-circle text-muted"
                    title="When checked, the annuity's growth exposure is excluded from the portfolio summary and solver calculations."
                    style={{ cursor: 'help' }}
                  ></i>
                </label>
              </div>
            )}

            <PortfolioSummary
              weights={activePortfolio.weights}
              growthExposure={activePortfolio.growthExposure}
              expectedReturn={activePortfolio.expectedReturn}
              volatility={activePortfolio.volatility}
              formData={formData}
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="btn-group">
        <button className="btn btn-secondary" onClick={prevStep}>
          Previous
        </button>
        <button
          className="btn text-white ms-2"
          style={{ backgroundColor: COLOURS.primary }}
          onClick={nextStep}
          disabled={!canProceed()}
        >
          Next
        </button>
      </div>
      </fieldset>
    </div>
  );
};

export default StepPortfolio;
